import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import moment from "moment";
import { v4 } from "uuid";
import Swal from "sweetalert2";
import { Shell } from "../../common/shell/shell.page";
import { PageHeader } from "../../common/components/page-header";
import { LoadingOverlay } from "../../common/components/loading-overlay";
import application from "../../core/application";
import { Permission } from "../../users/user.entity";
import { payableBillRepository } from "../payable-bill.repository";
import { PayableBill, type PayableRecurrence } from "../payable-bill.entity";
import { aiExtractPayableFromDocument } from "../../ai/callable-ai";
import { AI_CREDIT_COSTS, AiCreditCostHint, isInsufficientAiCreditsError } from "../../ai/ai-credit-guard";
import { readFileAsBase64 } from "../../ai/file-utils";
import { CashRegisterReportHero } from "../../cash-register/components/cash-register-report-hero";
import { useModuleAccess } from "../../settings/module-access";
import { ModuleLockedView } from "../../settings/components/module-locked-view";

const recurrenceOptions: { value: PayableRecurrence; label: string }[] = [
  { value: "none", label: "Não recorrente" },
  { value: "weekly", label: "Semanal" },
  { value: "monthly", label: "Mensal" },
  { value: "yearly", label: "Anual" },
];

export function PayableFormPage() {
  application.useHasPermission(Permission.ACCOUNTS_PAYABLE);
  const theme = useTheme();
  const navigate = useNavigate();
  const { billId } = useParams<{ billId: string }>();
  const isNew = !billId;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [dueDate, setDueDate] = useState(() => moment().format("YYYY-MM-DD"));
  const [payeeName, setPayeeName] = useState("");
  const [category, setCategory] = useState("");
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrence, setRecurrence] = useState<PayableRecurrence>("monthly");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const [aiOpen, setAiOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const moduleAccess = useModuleAccess();

  useEffect(() => {
    if (!billId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const b = await payableBillRepository.findById(billId);
        if (cancelled) return;
        setTitle(b.getTitle());
        setAmount(String(b.getAmount()));
        setDueDate(b.getDueDate().format("YYYY-MM-DD"));
        setPayeeName(b.getPayeeName());
        setCategory(b.getCategory() ?? "");
        setIsRecurring(b.getIsRecurring());
        let r = b.getRecurrence();
        if (b.getIsRecurring() && r === "none") {
          r = "monthly";
        }
        setRecurrence(r);
        setNotes(b.getNotes() ?? "");
      } catch {
        if (!cancelled) setError("Conta não encontrada.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [billId]);

  const applyAiResult = useCallback(
    (data: {
      title: string;
      amount?: number | null;
      dueDate: string;
      payeeName?: string | null;
      notes?: string;
    }) => {
      if (data.title) setTitle(data.title);
      if (typeof data.amount === "number" && !Number.isNaN(data.amount)) {
        setAmount(String(data.amount));
      }
      if (data.dueDate && /^\d{4}-\d{2}-\d{2}$/.test(data.dueDate)) {
        setDueDate(data.dueDate);
      }
      if (data.payeeName) setPayeeName(data.payeeName);
      if (data.notes) setNotes((n) => (n ? `${n}\n${data.notes}` : data.notes!));
    },
    []
  );

  const onPickFile = async (file: File | null) => {
    if (!file) return;
    const mime = file.type.split(";")[0].trim().toLowerCase();
    const ok =
      mime.startsWith("image/") ||
      mime === "application/pdf";
    if (!ok) {
      await Swal.fire({ icon: "error", title: "Formato inválido", text: "Use imagem ou PDF." });
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      await Swal.fire({ icon: "error", title: "Arquivo grande", text: "Máximo 12 MB." });
      return;
    }
    setAiOpen(false);
    setAiLoading(true);
    try {
      const base64 = await readFileAsBase64(file);
      const result = await aiExtractPayableFromDocument({
        documentBase64: base64,
        mimeType: mime || "application/octet-stream",
      });
      applyAiResult(result);
      await Swal.fire({ icon: "success", title: "Campos preenchidos", text: "Revise e salve." });
    } catch (e) {
      if (isInsufficientAiCreditsError(e)) return;
      await Swal.fire({
        icon: "error",
        title: "IA",
        text: e instanceof Error ? e.message : "Falha na leitura.",
      });
    } finally {
      setAiLoading(false);
    }
  };

  const save = async () => {
    const t = title.trim();
    if (!t) {
      setError("Informe o título.");
      return;
    }
    const amt = parseFloat(amount.replace(",", "."));
    if (Number.isNaN(amt) || amt < 0) {
      setError("Valor inválido.");
      return;
    }
    const due = moment(dueDate, "YYYY-MM-DD", true);
    if (!due.isValid()) {
      setError("Data de vencimento inválida.");
      return;
    }

    setError(null);
    setSaving(true);
    try {
      const now = moment();
      const rec: PayableRecurrence = isRecurring ? recurrence : "none";

      if (isNew) {
        const bill = new PayableBill(
          v4(),
          t,
          amt,
          due.clone().startOf("day"),
          payeeName.trim(),
          null,
          category.trim() || null,
          isRecurring,
          rec,
          "pending",
          null,
          null,
          notes.trim() || null,
          now,
          now
        );
        await payableBillRepository.save(bill);
      } else if (billId) {
        const bill = await payableBillRepository.findById(billId);
        bill.setTitle(t);
        bill.setAmount(amt);
        bill.setDueDate(due.clone().startOf("day"));
        bill.setPayeeName(payeeName.trim());
        bill.setCategory(category.trim() || null);
        bill.setIsRecurring(isRecurring);
        bill.setRecurrence(rec);
        bill.setNotes(notes.trim() || null);
        bill.touchUpdated();
        await payableBillRepository.save(bill);
      }
      navigate("/contas-a-pagar");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  if (loading || moduleAccess.loading) {
    return (
      <Shell title={isNew ? "Nova conta" : "Editar conta"}>
        <LoadingOverlay message="Carregando…" />
      </Shell>
    );
  }

  if (!moduleAccess.isEnabled("accounts_payable")) {
    return (
      <Shell title={isNew ? "Nova conta" : "Editar conta"}>
        <PageHeader
          title="Contas a pagar"
          description="Cadastro e edição de contas a pagar."
        />
        <ModuleLockedView
          moduleName="Contas a pagar"
          groupId="accounts_payable"
          details="Ative o módulo para registrar e editar contas a pagar."
          features={["Cadastro de contas a pagar", "Controle de vencimentos e recorrências", "Histórico de pagamentos"]}
        />
      </Shell>
    );
  }

  return (
    <Shell title={isNew ? "Nova conta" : "Editar conta"}>
      {saving ? <LoadingOverlay subtle message="Salvando…" /> : null}
      {aiLoading ? <LoadingOverlay subtle message="Analisando documento com IA…" /> : null}

      <PageHeader
        title={isNew ? "Nova conta a pagar" : "Editar conta"}
        description="Informe favorecido, valor e vencimento. Contas recorrentes avançam o próximo vencimento ao registrar pagamento."
      />

      <CashRegisterReportHero
        overline="Financeiro"
        title="Cadastro"
        chips={
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              startIcon={<AutoAwesomeOutlinedIcon />}
              onClick={() => setAiOpen(true)}
              sx={{ fontWeight: 700, textTransform: "none", borderRadius: 2 }}
            >
              Preencher com IA (foto ou PDF)
            </Button>
            <AiCreditCostHint cost={AI_CREDIT_COSTS.extractPayableFromDocument} />
          </Stack>
        }
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2.5}>
            <TextField
              label="Título / descrição"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              required
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Valor (R$)"
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                fullWidth
              />
              <TextField
                label="Vencimento"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            <TextField
              label="Favorecido (quem recebe)"
              value={payeeName}
              onChange={(e) => setPayeeName(e.target.value)}
              fullWidth
            />
            <TextField
              label="Categoria (opcional)"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              fullWidth
              placeholder="Ex.: Fornecedores, Utilidades, Impostos"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={isRecurring}
                  onChange={(_, v) => setIsRecurring(v)}
                />
              }
              label="Conta recorrente"
            />
            {isRecurring ? (
              <TextField
                select
                label="Periodicidade"
                value={recurrence === "none" ? "monthly" : recurrence}
                onChange={(e) => setRecurrence(e.target.value as PayableRecurrence)}
                fullWidth
              >
                {recurrenceOptions.filter((o) => o.value !== "none").map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            ) : null}
            <TextField
              label="Observações"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              multiline
              minRows={3}
            />
          </Stack>
        </CardContent>
      </Card>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 3 }}>
        <Button variant="contained" size="large" onClick={() => save()} sx={{ fontWeight: 800, borderRadius: 2 }}>
          Salvar
        </Button>
        <Button variant="outlined" onClick={() => navigate("/contas-a-pagar")} sx={{ textTransform: "none" }}>
          Voltar
        </Button>
      </Stack>

      <Dialog open={aiOpen} onClose={() => !aiLoading && setAiOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 800 }}>Documento (foto ou PDF)</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Envie uma foto nítida do boleto/fatura ou um PDF. A IA extrai dados para o formulário
            (Gemini nas Cloud Functions).
          </Typography>
          <Button variant="outlined" component="label" disabled={aiLoading} sx={{ borderRadius: 2 }}>
            Escolher arquivo
            <input
              type="file"
              hidden
              accept="image/*,application/pdf"
              onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
            />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAiOpen(false)} disabled={aiLoading}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <Paper
        variant="outlined"
        sx={{
          mt: 3,
          p: 2,
          borderRadius: 2,
          bgcolor: alpha(theme.palette.warning.main, 0.06),
        }}
      >
        <Typography variant="caption" color="text.secondary">
          A IA pode errar em leitura de valores ou datas. Sempre confira antes de salvar, principalmente
          em boletos com juros ou descontos.
        </Typography>
      </Paper>
    </Shell>
  );
}
